<template>
  <div class="migration-tools">
    <!-- 🎯 Header -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="pa-6 bg-gradient-to-r from-blue-500 to-cyan-600 text-white">
        <div class="d-flex align-center">
          <v-icon icon="mdi-database-sync" size="32" class="me-3" />
          <div>
            <h2 class="text-h4 mb-0">🔄 Herramientas de Migración</h2>
            <p class="text-subtitle-1 mb-0 opacity-90">Migración de datos y configuraciones</p>
          </div>
        </div>
      </v-card-title>
    </v-card>

    <!-- 📊 Migration Status -->
    <v-card class="mb-6" elevation="1">
      <v-card-title class="pa-4 bg-surface-variant">
        <v-icon icon="mdi-chart-timeline-variant" class="me-2" />
        Estado de Migraciones
      </v-card-title>
      <v-card-text class="pa-4">
        <v-row>
          <v-col cols="12" md="3" v-for="stat in migrationStats" :key="stat.label">
            <div class="text-center">
              <v-avatar :color="stat.color" size="48" class="mb-2">
                <v-icon :icon="stat.icon" color="white" size="24" />
              </v-avatar>
              <h3 class="text-h5 mb-1">{{ stat.value }}</h3>
              <p class="text-caption text-medium-emphasis">{{ stat.label }}</p>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 🛠️ Migration Tools Grid -->
    <v-row>
      <v-col
        v-for="tool in migrationTools"
        :key="tool.id"
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          :class="['migration-tool-card', { 'tool-disabled': tool.disabled }]"
          elevation="2"
          @click="!tool.disabled && executeTool(tool)"
        >
          <v-card-title class="pa-4">
            <div class="d-flex align-center">
              <v-avatar :color="tool.color" size="40" class="me-3">
                <v-icon :icon="tool.icon" color="white" />
              </v-avatar>
              <div class="flex-grow-1">
                <h3 class="text-h6 mb-0">{{ tool.name }}</h3>
                <p class="text-caption mb-0 text-medium-emphasis">{{ tool.category }}</p>
              </div>
              <v-chip
                v-if="tool.status"
                :color="tool.status === 'success' ? 'success' : tool.status === 'error' ? 'error' : 'warning'"
                size="small"
                variant="flat"
              >
                {{ tool.status }}
              </v-chip>
            </div>
          </v-card-title>

          <v-card-text class="pa-4 pt-0">
            <p class="text-body-2 mb-3">{{ tool.description }}</p>
            
            <!-- 📊 Progress Bar -->
            <div v-if="tool.progress !== undefined" class="mb-3">
              <div class="d-flex justify-space-between mb-1">
                <span class="text-caption">Progreso</span>
                <span class="text-caption">{{ tool.progress }}%</span>
              </div>
              <v-progress-linear
                :model-value="tool.progress"
                :color="tool.color"
                height="4"
                rounded
              />
            </div>

            <!-- ⏱️ Last Run -->
            <div v-if="tool.lastRun" class="text-caption text-medium-emphasis">
              <v-icon icon="mdi-clock-outline" size="12" class="me-1" />
              Última ejecución: {{ formatDate(tool.lastRun) }}
            </div>

            <!-- 🔧 Tool Actions -->
            <div class="mt-3">
              <v-chip-group>
                <v-chip
                  v-for="action in tool.actions"
                  :key="action"
                  size="small"
                  variant="outlined"
                  :color="tool.color"
                >
                  {{ action }}
                </v-chip>
              </v-chip-group>
            </div>
          </v-card-text>

          <v-card-actions class="pa-4 pt-0">
            <v-btn
              variant="outlined"
              size="small"
              :disabled="tool.disabled"
              @click.stop="configureTool(tool)"
            >
              Configurar
            </v-btn>
            <v-spacer />
            <v-btn
              :variant="tool.disabled ? 'outlined' : 'flat'"
              :color="tool.disabled ? 'grey' : tool.color"
              size="small"
              :disabled="tool.disabled"
              @click.stop="executeTool(tool)"
            >
              {{ tool.disabled ? 'No disponible' : 'Ejecutar' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- 📜 Migration Log -->
    <v-card class="mt-6" elevation="1">
      <v-card-title class="pa-4 bg-surface-variant">
        <v-icon icon="mdi-file-document-outline" class="me-2" />
        Registro de Migraciones
        <v-spacer />
        <v-btn
          variant="outlined"
          size="small"
          prepend-icon="mdi-refresh"
          @click="refreshLog"
        >
          Actualizar
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-0">
        <div class="log-container">
          <div
            v-for="(log, index) in migrationLog"
            :key="index"
            :class="['log-entry', `log-${log.type}`]"
          >
            <div class="d-flex align-center">
              <v-icon
                :icon="getLogIcon(log.type)"
                :color="getLogColor(log.type)"
                size="16"
                class="me-2"
              />
              <span class="text-caption me-2 text-medium-emphasis">{{ formatTime(log.timestamp) }}</span>
              <span class="flex-grow-1">{{ log.message }}</span>
              <v-chip
                v-if="log.tool"
                size="x-small"
                variant="outlined"
                color="primary"
              >
                {{ log.tool }}
              </v-chip>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- 🔄 Back to Dev Center -->
    <v-card class="mt-6" elevation="1">
      <v-card-actions class="pa-4">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-arrow-left"
          @click="$router.push('/dev-center')"
        >
          Volver al Dev Center
        </v-btn>
        <v-spacer />
        <v-btn
          color="warning"
          variant="flat"
          prepend-icon="mdi-backup-restore"
          @click="createBackup"
        >
          Crear Backup
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'

// 🎯 Types
interface MigrationTool {
  id: string
  name: string
  category: string
  description: string
  icon: string
  color: string
  progress?: number
  lastRun?: Date
  status?: 'success' | 'error' | 'warning'
  disabled?: boolean
  actions: string[]
}

interface LogEntry {
  timestamp: Date
  type: 'info' | 'success' | 'warning' | 'error'
  message: string
  tool?: string
}

// 🔧 Reactive state
const store = useMainStore()

// 📊 Mock data
const migrationStats = ref([
  { label: 'Migraciones Exitosas', value: '147', icon: 'mdi-check-circle', color: 'success' },
  { label: 'En Progreso', value: '3', icon: 'mdi-loading', color: 'warning' },
  { label: 'Errores', value: '2', icon: 'mdi-alert-circle', color: 'error' },
  { label: 'Total Ejecutadas', value: '152', icon: 'mdi-database', color: 'info' }
])

const migrationTools = ref<MigrationTool[]>([
  {
    id: '1',
    name: 'Migración de Usuarios',
    category: 'Autenticación',
    description: 'Migrar datos de usuarios desde sistema legacy',
    icon: 'mdi-account-multiple',
    color: 'blue',
    progress: 85,
    lastRun: new Date('2024-01-15'),
    status: 'success',
    actions: ['Validar', 'Migrar', 'Rollback']
  },
  {
    id: '2',
    name: 'Sincronización de Roles',
    category: 'Permisos',
    description: 'Sincronizar estructura de roles y permisos',
    icon: 'mdi-shield-account',
    color: 'purple',
    progress: 100,
    lastRun: new Date('2024-01-20'),
    status: 'success',
    actions: ['Sincronizar', 'Verificar']
  },
  {
    id: '3',
    name: 'Base de Datos Central',
    category: 'Datos',
    description: 'Migración completa de la base de datos',
    icon: 'mdi-database-sync',
    color: 'teal',
    progress: 45,
    lastRun: new Date('2024-01-22'),
    status: 'warning',
    actions: ['Migrar', 'Verificar', 'Optimizar']
  },
  {
    id: '4',
    name: 'Configuraciones',
    category: 'Sistema',
    description: 'Migrar configuraciones del sistema',
    icon: 'mdi-cog-sync',
    color: 'orange',
    lastRun: new Date('2024-01-18'),
    status: 'success',
    actions: ['Exportar', 'Importar']
  },
  {
    id: '5',
    name: 'Archivos Multimedia',
    category: 'Contenido',
    description: 'Migración de archivos e imágenes',
    icon: 'mdi-file-image',
    color: 'green',
    progress: 92,
    lastRun: new Date('2024-01-21'),
    status: 'success',
    actions: ['Copiar', 'Verificar']
  },
  {
    id: '6',
    name: 'Logs del Sistema',
    category: 'Monitoreo',
    description: 'Migración de logs históricos',
    icon: 'mdi-file-document',
    color: 'grey',
    disabled: true,
    actions: ['Archivar', 'Comprimir']
  }
])

const migrationLog = ref<LogEntry[]>([
  {
    timestamp: new Date(),
    type: 'info',
    message: 'Iniciando verificación de integridad de datos',
    tool: 'Base de Datos'
  },
  {
    timestamp: new Date(Date.now() - 60000),
    type: 'success',
    message: 'Migración de usuarios completada exitosamente (1,247 registros)',
    tool: 'Usuarios'
  },
  {
    timestamp: new Date(Date.now() - 120000),
    type: 'warning',
    message: 'Detectadas 3 configuraciones duplicadas durante la migración',
    tool: 'Configuraciones'
  },
  {
    timestamp: new Date(Date.now() - 180000),
    type: 'success',
    message: 'Sincronización de roles completada',
    tool: 'Roles'
  },
  {
    timestamp: new Date(Date.now() - 240000),
    type: 'error',
    message: 'Error en la conexión con el servidor legacy',
    tool: 'Conexión'
  }
])

// 🎯 Methods
const executeTool = (tool: MigrationTool) => {
  console.log('🔄 Ejecutando herramienta:', tool.name)
  store.showNotification({
    type: 'info',
    title: 'Migración Iniciada',
    message: `Ejecutando: ${tool.name}`
  })
  
  // Simular ejecución
  setTimeout(() => {
    store.showNotification({
      type: 'success',
      title: 'Migración Completada',
      message: `${tool.name} ejecutada exitosamente`
    })
  }, 2000)
}

const configureTool = (tool: MigrationTool) => {
  console.log('⚙️ Configurando herramienta:', tool.name)
  store.showNotification({
    type: 'info',
    title: 'Configuración',
    message: `Abriendo configuración para: ${tool.name}`
  })
}

const createBackup = () => {
  console.log('💾 Creando backup')
  store.showNotification({
    type: 'success',
    title: 'Backup Creado',
    message: 'Backup del sistema creado exitosamente'
  })
}

const refreshLog = () => {
  console.log('🔄 Actualizando log')
  migrationLog.value.unshift({
    timestamp: new Date(),
    type: 'info',
    message: 'Log actualizado manualmente',
    tool: 'Sistema'
  })
  
  store.showNotification({
    type: 'success',
    title: 'Log Actualizado',
    message: 'Registro de migraciones actualizado'
  })
}

const formatDate = (date: Date) => {
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

const formatTime = (date: Date) => {
  return date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getLogIcon = (type: string) => {
  const icons = {
    info: 'mdi-information',
    success: 'mdi-check-circle',
    warning: 'mdi-alert',
    error: 'mdi-alert-circle'
  }
  return icons[type as keyof typeof icons] || 'mdi-information'
}

const getLogColor = (type: string) => {
  const colors = {
    info: 'info',
    success: 'success',
    warning: 'warning',
    error: 'error'
  }
  return colors[type as keyof typeof colors] || 'info'
}

// 🎬 Lifecycle
onMounted(() => {
  console.log('🔄 Vista de herramientas de migración cargada')
})
</script>

<style scoped>
.migration-tools {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.migration-tool-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border-left: 4px solid transparent;
}

.migration-tool-card:hover:not(.tool-disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.tool-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.log-container {
  max-height: 400px;
  overflow-y: auto;
}

.log-entry {
  padding: 8px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  font-family: monospace;
  font-size: 12px;
}

.log-entry:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.log-info {
  border-left: 3px solid #2196f3;
}

.log-success {
  border-left: 3px solid #4caf50;
}

.log-warning {
  border-left: 3px solid #ff9800;
}

.log-error {
  border-left: 3px solid #f44336;
}

.bg-gradient-to-r {
  background: linear-gradient(90deg, #2196f3 0%, #00bcd4 100%);
}
</style>
